/**
 * MessageList Component - Scrollable Message Container
 *
 * Displays messages in chronological order with:
 * - Auto-scroll to bottom for new messages
 * - Manual scroll preservation when user scrolls up
 * - Loading states and empty state
 * - Infinite scroll for pagination (future)
 *
 * Scrolling Strategy:
 * - Auto-scroll if user is at bottom when new message arrives
 * - Preserve scroll position if user scrolled up (reading history)
 * - Force scroll to bottom on initial load
 */

import React, { useRef, useEffect, useState, useCallback } from 'react';
import { Box, Typography, CircularProgress, Avatar, AvatarGroup } from '@mui/material';
import { SmartToy as SmartToyIcon } from '@mui/icons-material';
import { WaitingIndicator } from '@trip-planner-monorepo/shared';
import MessageBubble from '../MessageBubble/MessageBubble';
import { useTypingStatus } from '../../hooks/useTypingStatus';
import './MessageList.css';

/**
 * MessageList Component
 *
 * @param {Object} props
 * @param {Array} props.messages - Array of message objects
 * @param {boolean} [props.loading=false] - Whether messages are loading
 * @param {boolean} [props.hasMore=false] - Whether more messages available
 * @param {Function} [props.onLoadMore] - Callback to load more messages
 * @param {string} props.currentUserId - Current user's ID for ownership check
 * @param {string} props.tripId - Trip ID for typing status
 * @param {string} [props.ownerId] - Trip owner ID (for shared trips)
 */
const MessageList = ({
  messages = [],
  loading = false,
  hasMore = false,
  onLoadMore,
  currentUserId,
  tripId,
  ownerId = null
}) => {
  const scrollContainerRef = useRef(null);
  const messagesEndRef = useRef(null);
  const isUserScrollingRef = useRef(false);
  const prevMessageCountRef = useRef(0);
  const prevLastMessageSignatureRef = useRef(null);
  const scrollSettleTimeoutRef = useRef(null);
  const scrollRafRef = useRef(null);
  const [isAITyping, setIsAITyping] = useState(false);
  const aiTypingTimeoutRef = useRef(null);
  const { typingUsers } = useTypingStatus(tripId, ownerId);

  /**
   * Check if user is at bottom of scroll container
   */
  const isAtBottom = () => {
    const container = scrollContainerRef.current;
    if (!container) return false;

    const threshold = 50; // pixels from bottom to consider "at bottom"
    const distanceFromBottom =
      container.scrollHeight - container.scrollTop - container.clientHeight;

    return distanceFromBottom < threshold;
  };

  /**
   * Scroll to bottom (smooth animation)
   */
  const scrollToBottom = useCallback((behavior = 'smooth') => {
    messagesEndRef.current?.scrollIntoView({ behavior, block: 'end' });
  }, []);

  /**
   * Force the container all the way to the bottom.
   * This compensates for late layout changes (e.g. long AI message wrapping)
   * where a single smooth scroll can stop slightly above the end.
   */
  const forceScrollToBottom = useCallback(() => {
    const container = scrollContainerRef.current;
    if (!container) return;

    container.scrollTop = container.scrollHeight;

    if (scrollRafRef.current) {
      cancelAnimationFrame(scrollRafRef.current);
    }
    scrollRafRef.current = requestAnimationFrame(() => {
      const currentContainer = scrollContainerRef.current;
      if (currentContainer) {
        currentContainer.scrollTop = currentContainer.scrollHeight;
      }
    });

    if (scrollSettleTimeoutRef.current) {
      clearTimeout(scrollSettleTimeoutRef.current);
    }
    scrollSettleTimeoutRef.current = setTimeout(() => {
      const currentContainer = scrollContainerRef.current;
      if (currentContainer) {
        currentContainer.scrollTop = currentContainer.scrollHeight;
      }
    }, 80);
  }, []);

  /**
   * Handle scroll event - track if user is manually scrolling
   */
  const handleScroll = () => {
    const atBottom = isAtBottom();
    isUserScrollingRef.current = !atBottom;

    // Future: Implement infinite scroll trigger here
    // if (scrollContainerRef.current.scrollTop < 100 && hasMore && onLoadMore) {
    //   onLoadMore();
    // }
  };

  /**
   * Detect AI mention and show typing indicator
   */
  useEffect(() => {
    if (messages.length === 0) return;

    const lastMessage = messages[messages.length - 1];
    const lastMessageText = lastMessage?.text ?? lastMessage?.content ?? '';

    // Check if last message mentioned AI and is from a user
    const mentionedAI = /@AI_Agent/i.test(lastMessageText);
    const isFromUser = lastMessage.senderId !== 'AI_AGENT';

    if (mentionedAI && isFromUser) {
      // Show AI typing indicator
      setIsAITyping(true);

      // Clear any existing timeout
      if (aiTypingTimeoutRef.current) {
        clearTimeout(aiTypingTimeoutRef.current);
      }

      // Fallback timeout (30 seconds)
      aiTypingTimeoutRef.current = setTimeout(() => {
        setIsAITyping(false);
      }, 30000);
    } else if (lastMessage.senderId === 'AI_AGENT') {
      // AI responded, clear typing indicator
      setIsAITyping(false);
      if (aiTypingTimeoutRef.current) {
        clearTimeout(aiTypingTimeoutRef.current);
      }
    }
  }, [messages]);

  /**
   * Cleanup AI typing timeout
   */
  useEffect(() => {
    return () => {
      if (aiTypingTimeoutRef.current) {
        clearTimeout(aiTypingTimeoutRef.current);
      }
    };
  }, []);

  /**
   * Auto-scroll effect when messages change.
   *
   * Handles both newly added and just-updated last messages (e.g. pending->sent,
   * text/timestamp updates). We auto-scroll when:
   * - user is at/near bottom, OR
   * - latest message is from current user, OR
   * - latest message is from AI.
   */
  useEffect(() => {
    if (messages.length === 0) {
      prevMessageCountRef.current = 0;
      prevLastMessageSignatureRef.current = null;
      return;
    }

    const isFirstMessage = messages.length === 1;
    const lastMessage = messages[messages.length - 1];
    const lastText = lastMessage?.text ?? lastMessage?.content ?? '';
    const lastTimestamp = lastMessage?.timestamp ?? '';
    const lastMessageSignature = `${lastMessage?.id || 'no-id'}|${lastTimestamp}|${lastText.length}`;
    const lastMessageChanged = prevLastMessageSignatureRef.current !== lastMessageSignature;

    const isAIMessage = lastMessage?.senderId === 'AI_AGENT';
    const isOwnMessage = lastMessage?.senderId === currentUserId;
    const shouldAutoScroll = !isUserScrollingRef.current || isAIMessage || isOwnMessage;

    if (isFirstMessage) {
      // First message: always scroll to bottom (instant)
      scrollToBottom('auto');
      forceScrollToBottom();
    } else if (lastMessageChanged && shouldAutoScroll) {
      // Keep the newest conversation in view for normal and AI messages.
      scrollToBottom('smooth');
      // Long messages can reflow after paint; pin to true bottom.
      forceScrollToBottom();
    }

    prevMessageCountRef.current = messages.length;
    prevLastMessageSignatureRef.current = lastMessageSignature;
  }, [messages, currentUserId, scrollToBottom, forceScrollToBottom]);

  /**
   * Auto-scroll when AI typing indicator appears
   *
   * When the AI starts "typing", scroll to show the indicator
   * so users can see the AI is processing their request
   */
  useEffect(() => {
    if (isAITyping) {
      scrollToBottom('smooth');
      forceScrollToBottom();
    }
  }, [isAITyping, scrollToBottom, forceScrollToBottom]);

  /**
   * Cleanup pending scroll callbacks/timeouts.
   */
  useEffect(() => {
    return () => {
      if (scrollSettleTimeoutRef.current) {
        clearTimeout(scrollSettleTimeoutRef.current);
      }
      if (scrollRafRef.current) {
        cancelAnimationFrame(scrollRafRef.current);
      }
    };
  }, []);

  /**
   * Loading state
   */
  if (loading && messages.length === 0) {
    return (
      <Box
        sx={{
          flex: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: 4
        }}
      >
        <CircularProgress size={32} />
      </Box>
    );
  }

  /**
   * Empty state
   */
  if (!loading && messages.length === 0) {
    return (
      <Box
        sx={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          padding: 4,
          textAlign: 'center'
        }}
      >
        <Typography
          variant="body1"
          sx={{
            color: 'text.secondary',
            marginBottom: 1
          }}
        >
          No messages yet
        </Typography>
        <Typography variant="caption" sx={{ color: 'text.disabled' }}>
          Start the conversation...
        </Typography>
      </Box>
    );
  }

  /**
   * Messages list
   */
  return (
    <Box
      ref={scrollContainerRef}
      onScroll={handleScroll}
      sx={{
        flex: 1,
        overflowY: 'auto',
        padding: '16px',
        display: 'flex',
        flexDirection: 'column',
        // Scrollbar styling
        '&::-webkit-scrollbar': {
          width: '6px'
        },
        '&::-webkit-scrollbar-track': {
          backgroundColor: 'rgba(0, 0, 0, 0.05)'
        },
        '&::-webkit-scrollbar-thumb': {
          backgroundColor: 'rgba(0, 0, 0, 0.2)',
          borderRadius: '3px',
          '&:hover': {
            backgroundColor: 'rgba(0, 0, 0, 0.3)'
          }
        }
      }}
    >
      {/* Messages */}
      {messages.map((message) => (
        <MessageBubble
          key={message.id}
          message={message}
          isOwn={message.senderId === currentUserId}
        />
      ))}

      {/* User Typing Indicator */}
      {typingUsers.length > 0 && (
        <Box className="user-typing-indicator">
          <AvatarGroup max={3} sx={{ justifyContent: 'flex-start' }}>
            {typingUsers.map((user) => (
              <Avatar
                key={user.userId}
                sx={{ width: 24, height: 24, fontSize: '0.75rem' }}
              >
                {user.userName.charAt(0).toUpperCase()}
              </Avatar>
            ))}
          </AvatarGroup>
          <Typography variant="caption" sx={{ color: 'text.secondary' }}>
            {typingUsers.length === 1
              ? `${typingUsers[0].userName} is typing...`
              : `${typingUsers.length} people are typing...`}
          </Typography>
          <CircularProgress size={12} sx={{ marginLeft: 'auto' }} />
        </Box>
      )}

      {/* AI Typing Indicator */}
      {isAITyping && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box className="ai-typing-indicator">
            <SmartToyIcon className="ai-icon" />
            <Typography variant="caption">AI is thinking</Typography>
          </Box>
          <WaitingIndicator size="small" message="" />
        </Box>
      )}

      {/* Scroll anchor */}
      <div ref={messagesEndRef} />
    </Box>
  );
};

export default MessageList;
